import { useState } from "react";
import { Loader2, Check, Download, ExternalLink } from "lucide-react";
import { downloadPhoto, photoFileBase } from "../utils/exportApplicantPhotos";
import { applicationPhotoUrl, profilePhotoUrl } from "../utils/exportUpgrades";

/**
 * Per-row download links for one applicant's two photographs.
 *
 * The bundle export on the header is for taking the whole cohort at once; this
 * is for the far commoner job of wanting one person's picture while looking at
 * their row, without opening the application or waiting for a zip.
 *
 * Each link downloads the file named `Name_MembershipNo`, the same as the
 * bundle — that naming is the entire point, and a plain <a download> cannot do
 * it across origins (see downloadPhoto).
 *
 * If the download fails, the button says so and turns into a link that opens
 * the raw file in a new tab. That fallback is not decoration: opening an image
 * in a tab needs no CORS grant, so it keeps working in the one case the fetch
 * cannot — and it tells the admin which of the two failed rather than leaving
 * them with a button that did nothing.
 */
export default function ApplicantPhotoLinks({ row }: { row: any }) {
  const base = photoFileBase(row);
  const application = applicationPhotoUrl(row);
  const profile = profilePhotoUrl(row);

  if (!application && !profile) {
    return <span className="text-gray-300 text-xs">No photo</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      <PhotoButton url={profile} fileBase={base} label="Profile" />
      <PhotoButton url={application} fileBase={base} label="Application" />
    </div>
  );
}

type State = "idle" | "working" | "done" | "failed";

function PhotoButton({
  url, fileBase, label,
}: {
  url: string;
  fileBase: string;
  label: string;
}) {
  const [state, setState] = useState<State>("idle");
  const [reason, setReason] = useState<string | null>(null);

  if (!url) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-gray-300" title={`No ${label.toLowerCase()} photo on file`}>
        {label} —
      </span>
    );
  }

  // Once it has failed the fetch is not offered again; the raw file is, which
  // is the thing that still works.
  if (state === "failed") {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        title={`Could not download it (${reason}). Opens the file in a new tab — right-click to save.`}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700 text-[11px] font-semibold hover:bg-amber-100"
      >
        <ExternalLink className="w-3 h-3" />
        {label}
      </a>
    );
  }

  return (
    <button
      onClick={async () => {
        setState("working");
        setReason(null);
        try {
          await downloadPhoto(url, fileBase);
          setState("done");
          // Back to a button after a moment: the same photograph is often
          // wanted twice, and a permanent tick reads as "already done".
          setTimeout(() => setState("idle"), 2500);
        } catch (e: any) {
          setReason(e?.message ? String(e.message) : "the download was blocked");
          setState("failed");
        }
      }}
      disabled={state === "working"}
      title={`Download as ${fileBase}.jpg`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-semibold disabled:opacity-60 ${
        state === "done"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-gray-200 text-gray-600 hover:bg-gray-50"
      }`}
    >
      {state === "working" ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : state === "done" ? (
        <Check className="w-3 h-3" />
      ) : (
        <Download className="w-3 h-3" />
      )}
      {label}
    </button>
  );
}
